import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import type { ExternalLinkType, InternalLinkType } from '@/types/app'
import { useUseNavigationLinksQuery } from './useNavigationLinks.generated'

type Link = (InternalLinkType | ExternalLinkType) & {
  callback: () => void
}

type ConditionalLink = {
  link: Link
  condition: boolean
}

export function useNavigationLinks(): ComputedRef<Link[]> {
  const { result, error } = useUseNavigationLinksQuery()
  if (error.value) {
    throw new Error(error.value.message)
  }

  const isLineActivationProvider = computed(() => {
    if (result.value == null) {
      return false
    }

    return result.value.uiControl.useLineMessageFeatures
  })

  // LINE連携のユーザーとやり取りするプランナーは、不要な機能があるためリンクを非表示にする
  const isNotLineActivationProvider = computed(() => {
    if (result.value == null) {
      return false
    }

    return !result.value.uiControl.useLineMessageFeatures
  })

  const showReportPage = computed(() => {
    if (result.value == null) {
      return false
    }

    return (
      result.value.uiControl.viewCorporationReport ||
      result.value.uiControl.viewBranchReport
    )
  })

  const NOOP = () => {
    // do nothing
  }

  const links = computed((): Link[] => {
    const navigations: ConditionalLink[] = [
      {
        link: {
          to: { name: 'dashboard' },
          text: 'ダッシュボード',
          exact: true,
          callback: NOOP,
        },
        condition: true,
      },
      {
        link: {
          to: { name: 'chat' },
          text: 'チャット',
          callback: NOOP,
        },
        condition: true,
      },
      {
        link: {
          to: { name: 'karteList' },
          text: 'ユーザーを探す',
          callback: NOOP,
        },
        condition: isNotLineActivationProvider.value,
      },
      {
        link: {
          to: { name: 'membersReport' },
          text: '社内レポート',
          callback: NOOP,
        },
        condition: showReportPage.value,
      },
      {
        link: {
          to: { name: 'chatMessageSendingJobIndex' },
          text: '配信',
          callback: NOOP,
        },
        condition: isLineActivationProvider.value,
      },
      {
        link: {
          to: { name: 'EnqueteIndexPage' },
          text: 'アンケート',
          callback: NOOP,
        },
        condition: isLineActivationProvider.value,
      },
      {
        link: {
          to: { name: 'news' },
          text: 'お知らせ',
          callback: NOOP,
        },
        condition: true,
      },
      {
        link: {
          to: { name: 'seminarList' },
          text: 'セミナー',
          callback: NOOP,
        },
        condition: isNotLineActivationProvider.value,
      },
      {
        link: {
          to: { name: 'solicitorCloud' },
          text: 'スキルフォース',
          callback: NOOP,
        },
        condition: true,
      },
      {
        link: {
          to: { name: 'OkaneKollegeIndex' },
          text: 'オカネカレッジ',
          callback: NOOP,
        },
        condition: result.value?.viewer.is400FOkaneKollegeStaff ?? false,
      },
    ]

    return navigations
      .filter(({ condition }): boolean => condition)
      .map(({ link }: ConditionalLink): Link => link)
  })

  return links
}
